import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const StudentGrievance = ({ onNavigate, userInfo }) => {
  // Layout State
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  // Page Specific State
  const [activeTab, setActiveTab] = useState('submit');
  const [selectedCategory, setSelectedCategory] = useState('academic');
  const [grievanceForm, setGrievanceForm] = useState({
    category: 'academic',
    subject: '',
    description: '',
    priority: 'medium',
    attachments: []
  });

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const [grievancesData, setGrievancesData] = useState([]);
  const [grievanceStats, setGrievanceStats] = useState({
    total: 0,
    submitted: 0,
    underReview: 0,
    resolved: 0,
    rejected: 0
  });

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch Grievance Data from API
  useEffect(() => {
    const fetchGrievanceData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        // Fetch grievances data
        const grievanceResponse = await fetch(`${API_LINK}/api/student/grievances`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!grievanceResponse.ok) {
          if (grievanceResponse.status === 401) {
            window.location.href = '/';
            return;
          } else if (grievanceResponse.status === 404) {
            setError('Grievance information not available.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${grievanceResponse.status}`);
        }

        const grievanceResult = await grievanceResponse.json();

        if (grievanceResult.success && grievanceResult.data) {
          const data = grievanceResult.data;
          setGrievancesData(data.grievances || []);
          setGrievanceStats(data.stats || {
            total: 0,
            submitted: 0,
            underReview: 0,
            resolved: 0,
            rejected: 0
          });
        } else {
          setError(grievanceResult.message || 'Failed to fetch grievance data');
        }

      } catch (error) {
        console.error('Error fetching grievance data:', error);
        setError(`Failed to load grievance data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure component is mounted
    const timer = setTimeout(fetchGrievanceData, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Grievance categories
  const grievanceCategories = [
    { id: 'academic', name: 'Academic Issues', icon: '📚', color: '#238548' },
    { id: 'administrative', name: 'Administrative', icon: '📋', color: '#3498db' },
    { id: 'examination', name: 'Examination', icon: '📝', color: '#e74c3c' },
    { id: 'fees', name: 'Fee Related', icon: '💰', color: '#f39c12' },
    { id: 'hostel', name: 'Hostel Issues', icon: '🏠', color: '#9b59b6' },
    { id: 'infrastructure', name: 'Infrastructure', icon: '🏗️', color: '#2ecc71' },
    { id: 'faculty', name: 'Faculty Related', icon: '👨‍🏫', color: '#34495e' },
    { id: 'other', name: 'Other Issues', icon: '📞', color: '#e67e22' }
  ];

  // Handle form submission
  const handleSubmitGrievance = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setSubmitError('Authentication token not found. Please login again.');
        return;
      }

      // Validate form
      if (!grievanceForm.subject.trim()) {
        setSubmitError('Subject is required');
        return;
      }

      if (!grievanceForm.description.trim()) {
        setSubmitError('Description is required');
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('category', grievanceForm.category);
      formData.append('subject', grievanceForm.subject.trim());
      formData.append('description', grievanceForm.description.trim());
      formData.append('priority', grievanceForm.priority);

      // Add attachments
      grievanceForm.attachments.forEach((file, index) => {
        formData.append('attachments', file);
      });

      const response = await fetch(`${API_LINK}/api/student/grievances`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type for FormData
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 401) {
          setSubmitError('Session expired. Please login again.');
          window.location.href = '/';
          return;
        }

        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit grievance');
      }

      const result = await response.json();

      if (result.success) {
        setSubmitSuccess('Grievance submitted successfully! You will receive a response within 48 hours.');

        // Reset form
        setGrievanceForm({
          category: 'academic',
          subject: '',
          description: '',
          priority: 'medium',
          attachments: []
        });

        // Refresh grievances data
        setTimeout(() => {
          window.location.reload(); // Simple refresh to get latest data
        }, 2000);

      } else {
        throw new Error(result.message || 'Failed to submit grievance');
      }

    } catch (error) {
      console.error('Error submitting grievance:', error);
      setSubmitError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);

    // Validate files
    const validFiles = [];
    let hasError = false;

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        setSubmitError('Each file must be less than 5MB');
        hasError = true;
        break;
      }

      if (!file.type.match(/^(image\/|application\/pdf|application\/msword|application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document)/)) {
        setSubmitError('Only images, PDF, DOC, and DOCX files are allowed');
        hasError = true;
        break;
      }

      validFiles.push(file);
    }

    if (!hasError) {
      setGrievanceForm(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...validFiles]
      }));
      setSubmitError(null);
    }
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setGrievanceForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return '#f39c12';
      case 'under_review': return '#3498db';
      case 'resolved': return '#2ecc71';
      case 'rejected': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'submitted': return 'Submitted';
      case 'under_review': return 'Under Review';
      case 'resolved': return 'Resolved';
      case 'rejected': return 'Rejected';
      default: return 'Unknown';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return '#2ecc71';
      case 'medium': return '#f39c12';
      case 'high': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Grievance Portal...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Grievance Portal
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Try Again
            </button>

            <button
              onClick={() => window.location.href = '/'}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="student"
      onNavigate={onNavigate}
      activePage="student-grievance"
      pageTitle="Grievance Portal"
    >
      {/* Grievance Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          background: '#238548',
          color: '#ffffff',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <h1 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '2rem',
            fontWeight: 'bold'
          }}>
            ❗ Student Grievance Portal
          </h1>
          <p style={{
            margin: 0,
            fontSize: '1.1rem',
            opacity: 0.9
          }}>
            Submit your complaints and track their resolution status
          </p>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
          gap: '1rem',
          padding: '1.5rem',
          backgroundColor: '#f8f9fa'
        }}>
          {[
            { label: 'Total', value: grievanceStats.total, color: '#238548' },
            { label: 'Submitted', value: grievanceStats.submitted, color: '#f39c12' },
            { label: 'Under Review', value: grievanceStats.underReview, color: '#3498db' },
            { label: 'Resolved', value: grievanceStats.resolved, color: '#2ecc71' },
            { label: 'Rejected', value: grievanceStats.rejected, color: '#e74c3c' }
          ].map((stat, index) => (
            <div key={index} style={{
              textAlign: 'center',
              padding: '1rem',
              background: '#ffffff',
              borderRadius: '8px',
              border: `2px solid ${stat.color}`,
              transition: 'transform 0.3s ease'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
              <div style={{
                fontSize: '1.8rem',
                fontWeight: 'bold',
                color: stat.color,
                marginBottom: '0.5rem'
              }}>
                {stat.value}
              </div>
              <div style={{
                fontSize: '0.9rem',
                color: '#666',
                fontWeight: '500'
              }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #ecf0f1'
        }}>
          {[
            { id: 'submit', label: 'Submit Grievance', icon: '📝' },
            { id: 'track', label: 'Track Grievances', icon: '📊' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                background: activeTab === tab.id ? '#238548' : 'transparent',
                color: activeTab === tab.id ? '#ffffff' : '#666',
                border: 'none',
                padding: '1rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Success/Error Messages */}
      {(submitSuccess || submitError || error) && (
        <div style={{
          background: submitSuccess ? '#d4edda' : '#f8d7da',
          border: `1px solid ${submitSuccess ? '#c3e6cb' : '#f5c6cb'}`,
          color: submitSuccess ? '#155724' : '#721c24',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.2rem' }}>
            {submitSuccess ? '✅' : '❌'}
          </span>
          {submitSuccess || submitError || error}
          <button
            onClick={() => {
              setSubmitSuccess(null);
              setSubmitError(null);
              setError(null);
            }}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: '1.2rem'
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'submit' ? (
        <>
          {/* Submit Grievance Form */}
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            padding: '2rem',
            marginBottom: '2rem'
          }}>
            <h2 style={{
              margin: '0 0 1.5rem 0',
              color: '#238548',
              fontSize: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>📝</span>
              Submit New Grievance
            </h2>

            <form onSubmit={handleSubmitGrievance}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                {/* Category Selection */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Category <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <select
                    value={grievanceForm.category}
                    onChange={(e) => setGrievanceForm(prev => ({ ...prev, category: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #ecf0f1',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#ecf0f1';
                    }}
                  >
                    {grievanceCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Priority Selection */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Priority <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <select
                    value={grievanceForm.priority}
                    onChange={(e) => setGrievanceForm(prev => ({ ...prev, priority: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #ecf0f1',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#ecf0f1';
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              {/* Subject */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Subject <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="text"
                  value={grievanceForm.subject}
                  onChange={(e) => setGrievanceForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Brief description of your grievance"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #ecf0f1',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#238548';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#ecf0f1';
                  }}
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Detailed Description <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <textarea
                  value={grievanceForm.description}
                  onChange={(e) => setGrievanceForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Provide detailed information about your grievance..."
                  required
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #ecf0f1',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.3s',
                    resize: 'vertical'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#238548';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#ecf0f1';
                  }}
                />
              </div>

              {/* File Upload */}
              <div style={{ marginBottom: '2rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Supporting Documents (Optional)
                </label>
                <div style={{
                  border: '2px dashed #97d6ac',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  backgroundColor: '#f8fffe'
                }}>
                  <input
                    type="file"
                    id="grievance-files"
                    multiple
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor="grievance-files"
                    style={{
                      display: 'inline-block',
                      background: '#238548',
                      color: '#ffffff',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#1e7040';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#238548';
                    }}
                  >
                    📎 Attach Files
                  </label>
                  <p style={{
                    margin: '0.5rem 0 0 0',
                    color: '#666',
                    fontSize: '0.9rem'
                  }}>
                    Supported formats: Images, PDF, DOC, DOCX (Max 5MB each)
                  </p>
                </div>

                {/* Attached Files */}
                {grievanceForm.attachments.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <h4 style={{
                      margin: '0 0 0.5rem 0',
                      color: '#333',
                      fontSize: '1rem'
                    }}>
                      Attached Files:
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {grievanceForm.attachments.map((file, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.5rem 0.75rem',
                          background: '#f8f9fa',
                          borderRadius: '6px'
                        }}>
                          <span style={{ fontSize: '0.9rem', color: '#333' }}>{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            style={{
                              background: '#e74c3c',
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '0.25rem 0.5rem',
                              fontSize: '0.8rem',
                              cursor: 'pointer'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                style={{
                  background: submitting ? '#95a5a6' : '#238548',
                  color: '#ffffff',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  opacity: submitting ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.backgroundColor = '#1e7040';
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.backgroundColor = '#238548';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                <span>{submitting ? '⏳' : '📤'}</span>
                {submitting ? 'Submitting...' : 'Submit Grievance'}
              </button>
            </form>
          </div>

          {/* Guidelines */}
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            padding: '2rem'
          }}>
            <h3 style={{
              margin: '0 0 1rem 0',
              color: '#238548',
              fontSize: '1.3rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>📋</span>
              Grievance Guidelines
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '1.5rem'
            }}>
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Before Submitting:</h4>
                <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#666' }}>
                  <li>Ensure you have tried to resolve the issue through proper channels</li>
                  <li>Collect all relevant documents and evidence</li>
                  <li>Be specific and clear in your description</li>
                  <li>Choose the appropriate category and priority level</li>
                </ul>
              </div>
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Response Timeline:</h4>
                <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#666' }}>
                  <li>High Priority: Within 24 hours</li>
                  <li>Medium Priority: Within 48 hours</li>
                  <li>Low Priority: Within 72 hours</li>
                  <li>You will receive updates via email and portal</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* Track Grievances */
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>📊</span>
            <h2 style={{ margin: 0, fontSize: '1.3rem' }}>Your Grievances</h2>
          </div>

          <div style={{ padding: '1.5rem' }}>
            {grievancesData.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#666'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>No Grievances Found</h3>
                <p style={{ margin: 0 }}>You haven't submitted any grievances yet.</p>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5rem'
              }}>
                {grievancesData.map((grievance) => (
                  <div key={grievance.id} style={{
                    border: '1px solid #ecf0f1',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                    {/* Grievance Header */}
                    <div style={{
                      background: grievanceCategories.find(c => c.id === grievance.category)?.color || '#238548',
                      color: '#ffffff',
                      padding: '1rem 1.5rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '1rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                      }}>
                        <span style={{ fontSize: '1.5rem' }}>
                          {grievanceCategories.find(c => c.id === grievance.category)?.icon || '📝'}
                        </span>
                        <div>
                          <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.1rem' }}>
                            {grievance.subject}
                          </h3>
                          <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>
                            ID: {grievance.grievanceId || grievance.id}
                          </p>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '0.75rem',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          backgroundColor: getPriorityColor(grievance.priority),
                          color: '#ffffff'
                        }}>
                          {grievance.priority.charAt(0).toUpperCase() + grievance.priority.slice(1)} Priority
                        </span>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          backgroundColor: getStatusColor(grievance.status),
                          color: '#ffffff'
                        }}>
                          {getStatusText(grievance.status)}
                        </span>
                      </div>
                    </div>

                    {/* Grievance Content */}
                    <div style={{ padding: '1.5rem' }}>
                      <p style={{
                        margin: '0 0 1rem 0',
                        color: '#666',
                        lineHeight: 1.6
                      }}>
                        {grievance.description}
                      </p>

                      {/* Dates */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                        gap: '1rem',
                        marginBottom: '1rem'
                      }}>
                        <div>
                          <span style={{
                            display: 'block',
                            fontSize: '0.8rem',
                            color: '#999',
                            marginBottom: '0.25rem'
                          }}>
                            Submitted On
                          </span>
                          <span style={{
                            fontSize: '1rem',
                            fontWeight: '500',
                            color: '#333'
                          }}>
                            {formatDate(grievance.submittedOn || grievance.createdAt)}
                          </span>
                        </div>
                        <div>
                          <span style={{
                            display: 'block',
                            fontSize: '0.8rem',
                            color: '#999',
                            marginBottom: '0.25rem'
                          }}>
                            Last Updated
                          </span>
                          <span style={{
                            fontSize: '1rem',
                            fontWeight: '500',
                            color: '#333'
                          }}>
                            {formatDate(grievance.lastUpdated || grievance.updatedAt)}
                          </span>
                        </div>
                      </div>

                      {/* Response */}
                      {grievance.response && (
                        <div style={{
                          background: '#f8f9fa',
                          borderLeft: '4px solid #238548',
                          padding: '1rem',
                          borderRadius: '0 8px 8px 0'
                        }}>
                          <h4 style={{
                            margin: '0 0 0.5rem 0',
                            color: '#238548',
                            fontSize: '1rem'
                          }}>
                            Administrative Response:
                          </h4>
                          <p style={{
                            margin: 0,
                            color: '#666',
                            fontSize: '0.95rem'
                          }}>
                            {grievance.response}
                          </p>
                        </div>
                      )}

                      {/* Attachments */}
                      {grievance.attachments && grievance.attachments.length > 0 && (
                        <div style={{ marginTop: '1rem' }}>
                          <h4 style={{
                            margin: '0 0 0.5rem 0',
                            color: '#238548',
                            fontSize: '1rem'
                          }}>
                            📎 Attachments:
                          </h4>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.5rem'
                          }}>
                            {grievance.attachments.map((attachment, index) => (
                              <a
                                key={index}
                                href={`${API_LINK}${attachment.path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#e9ecef',
                                  borderRadius: '6px',
                                  textDecoration: 'none',
                                  color: '#495057',
                                  fontSize: '0.9rem',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#dee2e6';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = '#e9ecef';
                                }}
                              >
                                📄 {attachment.originalName}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default StudentGrievance;